import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import ScrollToTop from "./components/ScrollToTop";
import Landing from "./pages/Landing";
import Discover from "./pages/Discover";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Pricing from "./pages/Pricing";
import Wallet from "./pages/Wallet";
import MerchantDashboard from "./pages/MerchantDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminSlides from "./pages/AdminSlides";
import AdminHomeSections from "./pages/AdminHomeSections";
import AdminCompanies from "./pages/AdminCompanies";
import AdminPlans from "./pages/AdminPlans";
import AdminCities from "./pages/AdminCities";
import AdminSettings from "./pages/AdminSettings";
import AdminHighlights from "./pages/AdminHighlights";
import AdminQuickRegister from "./pages/AdminQuickRegister";
import AdminReport from "./pages/AdminReport";
import AdminOsmSearch from "./pages/AdminOsmSearch";
import AdminWallets from "./pages/AdminWallets";
import AdminNotifications from "./pages/AdminNotifications";
import LandingForBusiness from "./pages/LandingForBusiness";
import LandingForCustomers from "./pages/LandingForCustomers";
import CitiesPublic from "./pages/CitiesPublic";
import Checkout from "./pages/Checkout";
import CheckoutResult from "./pages/CheckoutResult";
import CompanyDetail from "./pages/CompanyDetail";
import GuideViewer from "./pages/GuideViewer";
import MyOrders from "./pages/MyOrders";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import AdminUsers from "./pages/AdminUsers";
import Profile from "./pages/Profile";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import AdminSegments from "./pages/AdminSegments";
import AdminBlog from "./pages/AdminBlog";
import AdminCompanyCategories from "./pages/AdminCompanyCategories";
import MerchantPackages from "./pages/MerchantPackages";
import MerchantSubscriptions from "./pages/MerchantSubscriptions";
import AdminPackages from "./pages/AdminPackages";
import CustomerVouchers from "./pages/CustomerVouchers";
import ContractSign from "./pages/ContractSign";
import VoucherLanding from "./pages/VoucherLanding";
import AdminPayouts from "./pages/AdminPayouts";
import AdminPendingMerchants from "./pages/AdminPendingMerchants";
import VerifyEmail from "./pages/VerifyEmail";
import { ForgotPassword, ResetPassword } from "./pages/PasswordRecovery";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import BottomTabBar from "./components/BottomTabBar";
import { CartProvider } from "./context/CartContext";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-stone-500">Carregando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function Shell() {
  return (
    <>
      <Header />
      <div className="pb-20 lg:pb-0">
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/discover/:citySlug" element={<Discover />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/wallet" element={<Protected roles={["CUSTOMER", "ADMIN"]}><Wallet /></Protected>} />
        <Route path="/orders" element={<Protected roles={["CUSTOMER"]}><MyOrders /></Protected>} />
        <Route path="/profile" element={<Protected roles={["CUSTOMER", "MERCHANT", "ADMIN"]}><Profile /></Protected>} />
        <Route path="/merchant" element={<Protected roles={["MERCHANT", "ADMIN"]}><MerchantDashboard /></Protected>} />
        <Route path="/merchant/packages" element={<Protected roles={["MERCHANT", "ADMIN"]}><MerchantPackages /></Protected>} />
        <Route path="/merchant/subscriptions/:companyId" element={<Protected roles={["MERCHANT", "ADMIN"]}><MerchantSubscriptions /></Protected>} />
        <Route path="/admin/packages" element={<Protected roles={["ADMIN"]}><AdminPackages /></Protected>} />
        <Route path="/vouchers" element={<Protected roles={["CUSTOMER"]}><CustomerVouchers /></Protected>} />
        <Route path="/contract/sign/:token" element={<ContractSign />} />
        <Route path="/v/:code" element={<VoucherLanding />} />
        <Route path="/admin/payouts" element={<Protected roles={["ADMIN"]}><AdminPayouts /></Protected>} />
        <Route path="/admin/pending-merchants" element={<Protected roles={["ADMIN"]}><AdminPendingMerchants /></Protected>} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/admin" element={<Protected roles={["ADMIN"]}><AdminDashboard /></Protected>} />
        <Route path="/admin/slides" element={<Protected roles={["ADMIN"]}><AdminSlides /></Protected>} />
        <Route path="/admin/home-sections" element={<Protected roles={["ADMIN"]}><AdminHomeSections /></Protected>} />
        <Route path="/admin/segments" element={<Protected roles={["ADMIN"]}><AdminSegments /></Protected>} />
        <Route path="/admin/blog" element={<Protected roles={["ADMIN"]}><AdminBlog /></Protected>} />
        <Route path="/admin/company-categories" element={<Protected roles={["ADMIN"]}><AdminCompanyCategories /></Protected>} />
        <Route path="/admin/companies" element={<Protected roles={["ADMIN"]}><AdminCompanies /></Protected>} />
        <Route path="/admin/users" element={<Protected roles={["ADMIN"]}><AdminUsers /></Protected>} />
        <Route path="/admin/plans" element={<Protected roles={["ADMIN"]}><AdminPlans /></Protected>} />
        <Route path="/admin/cities" element={<Protected roles={["ADMIN"]}><AdminCities /></Protected>} />
        <Route path="/admin/settings" element={<Protected roles={["ADMIN"]}><AdminSettings /></Protected>} />
        <Route path="/admin/highlights" element={<Protected roles={["ADMIN"]}><AdminHighlights /></Protected>} />
        <Route path="/admin/quick-register" element={<Protected roles={["ADMIN"]}><AdminQuickRegister /></Protected>} />
        <Route path="/admin/report" element={<Protected roles={["ADMIN"]}><AdminReport /></Protected>} />
        <Route path="/admin/osm-search" element={<Protected roles={["ADMIN"]}><AdminOsmSearch /></Protected>} />
        <Route path="/admin/wallets" element={<Protected roles={["ADMIN"]}><AdminWallets /></Protected>} />
        <Route path="/admin/notifications" element={<Protected roles={["ADMIN"]}><AdminNotifications /></Protected>} />
        <Route path="/para-empresas" element={<LandingForBusiness />} />
        <Route path="/para-clientes" element={<LandingForCustomers />} />
        <Route path="/cidades" element={<CitiesPublic />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/guia/:slug" element={<GuideViewer />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/checkout/success" element={<CheckoutResult status="success" />} />
        <Route path="/checkout/pending" element={<CheckoutResult status="pending" />} />
        <Route path="/checkout/failure" element={<CheckoutResult status="failure" />} />
        <Route path="/company/:id" element={<CompanyDetail />} />
      </Routes>
      </div>
      <BottomTabBar />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <CartProvider>
          <Shell />
          <PWAInstallPrompt />
          <Toaster position="top-right" richColors />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
